/**
 * The only way an error reaches the screen.
 *
 * It takes an `ApiError` and renders the Turkish sentence for its *kind*. The
 * server's `detail` is never displayed - it can carry a SQLAlchemy message, an
 * exception name or a filesystem path, none of which belong in front of a user
 * and all of which would be a small information leak. It goes to the console,
 * where whoever is debugging will look for it.
 */

import { useEffect } from "react";
import type { ApiError } from "@/api/errors";
import { ERROR_MESSAGES } from "@/i18n/terms";
import { Button, Callout } from "./ui";

export function errorMessage(error: ApiError): string {
  return ERROR_MESSAGES[error.kind] ?? ERROR_MESSAGES.unknown;
}

export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: ApiError;
  onRetry?: () => void;
  compact?: boolean;
}) {
  useEffect(() => {
    // Diagnostics live here, not in the DOM.
    console.error("[sentyform]", error.kind, error.status, error.detail, error.url);
  }, [error]);

  const tone = error.kind === "not_found" ? "neutral" : "danger";
  const body = (
    <div className="stack-tight">
      <span>{errorMessage(error)}</span>
      {onRetry && error.retryable && (
        <div>
          <Button onClick={onRetry}>Tekrar dene</Button>
        </div>
      )}
    </div>
  );

  if (compact) {
    return <Callout tone={tone}>{body}</Callout>;
  }
  return (
    <Callout tone={tone} title="İşlem tamamlanamadı">
      {body}
    </Callout>
  );
}
